import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { useStore } from "../../helpers";
import TeamDetails from "./team-details";
import { AddTeam } from "./add-team";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CreateModel, LoadingState, SideOverlay } from "../../components";
import { useGetFullProject, useIsMobile } from "../../custom-hooks";
import { useQuery } from "@tanstack/react-query";
import UsersList from "./users-list";
import { AddNewUsers } from "./add-users";
import AsyncSelect from "react-select/async";
import { UserDetails } from "./user-details";
import { FTPBreadCrumbs, FTPSearchBar } from "../../components-new";
import { PageShell } from "../../layouts/PageShell";
import { VortexSpinner } from "../digital-repository";

const UserManagement = observer((props) => {
  const rootStore = useStore();
  const isMobile = useIsMobile();
  const { userStore, projectStore, voterListStore } = rootStore;
  const { editTeam, addTeam } = userStore;
  const { showVoterlist, FilterStatus } = voterListStore;
  const { projectid } = useParams();
  const selectedTagsRef = useRef(null);

  const crumbs = [
    { name: "DashBoard", href: `/project/${projectid}`, current: false },
    { name: "User Management", href: "#", current: true },
  ];

  const [query, setQuery] = useState("");
  const [userData, setUserData] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [userFilterData, setUserFilterData] = useState({});
  const [TeamList, setTeamList] = useState({});
  const userProfile = userStore.user;
  const role = userProfile["role"];
  const isAllowed = role === "root" || role === "client";
  const { t } = useTranslation(["user_management", "survey_management"]);

  const { isLoading: fullProjectLoading, refetch: reftechFullProject } =
    useGetFullProject();

  useEffect(() => {
    setTeamList(projectStore.getProjectProperty("user_hierarchy", {}));
  }, []);

  const getData = async (data: object) => {
    setUserFilterData({ data });
  };

  const getTeamData = async (data: object) => {
    setTeamList(data);
  };

  const get_users = async () => {
    const payload = {
      projectid: parseInt(projectid),
      // tags: selectedTags.map((x) => {
      //   return x["value"];
      // }),
      tags: selectedTags.map((x) => {
        return x["value"];
      }),
      query: query,
      limit: 500,
      offset: 0,
    };
    return await projectStore.getAllUsers(payload);
  };

  const { data, isLoading, isSuccess, refetch, isFetching } = useQuery({
    // queryKey: ["users", projectid],
    queryKey: ["users", projectid, selectedTags, query],

    queryFn: get_users,
    onSuccess: (data) => {
      setUserData(data?.data);
      userStore.setUserList(data?.data["users"]);
    },
  });

  const fetchAllTags = async () => {
    const response = await projectStore.getAllUserTags({
      projectid: parseInt(projectid),
    });

    try {
      if (response.status === 200) {
        return response.data?.map((val) => {
          return {
            value: val,
            label: val,
          };
        });
      } else {
        return [];
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleAddTeamClose = () => {
    userStore.setAddTeamStatus(false);
  };

  const UserAttachVoterProfile = async () => {
    const payload = {
      userid: userStore.userId,
      voter: voterListStore.activeVoter,
    };

    const result = await voterListStore.attach_VoterProfile(payload);
    if (result.status === 200) {
      voterListStore.update_connect_user_to_voterlist_modal(false);
      voterListStore.setShowVoterlist(false);
      toast.success("Attached Successfully");
    }
  };

  const handleSearch = (val) => {
    setQuery(val);
    setTimeout(() => refetch(), 500);
  };

  if (isLoading || fullProjectLoading) return <LoadingState />;

  // Header actions component
  const headerActions = (
    <div
      className={
        "flex flex-col justify-end flex-1 md:gap-x-4 sm:flex-row sm:items-center flex-wrap gap-5"
      }
    >
      <VortexSpinner visible={isFetching} />

      <FTPSearchBar
        onSearch={handleSearch}
        placeHolder="Search Users"
        clientSideSearch={false}
      />

      <div className="flex items-center gap-2 w-full lg:w-1/2 relative">
        {/* Async Select Dropdown */}
        <div className="flex-1">
          <AsyncSelect
            ref={selectedTagsRef}
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={fetchAllTags}
            value={selectedTags}
            // @ts-ignore ̰
            onChange={(selected) => setSelectedTags(selected || [])}
            placeholder="Filter by tags..."
            className="w-full"
            styles={{
              control: (provided, state) => ({
                ...provided,
                minHeight: "48px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
              }),
              menu: (provided) => ({
                ...provided,
              }),
              menuPortal: (base) => ({
                ...base,
              }),
              indicatorsContainer: (provided) => ({
                ...provided,
                padding: "0 8px",
              }),
            }}
            menuPortalTarget={document.body}
          />
        </div>

        {/* <button
          disabled={isFetching}
          onClick={() => {
            toast.info("Filtering users...");
            refetch();
          }}
          className={`${isFetching
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
            } text-white font-medium px-5 py-2.5 rounded-md shadow-sm transition-all duration-200 whitespace-nowrap`}
        >
          {isFetching ? "Filtering..." : "Filter Tags"}
        </button> */}

        {/* <button
          onClick={() => {
            setSelectedTags([]);
            selectedTagsRef.current?.clearValue();
            toast.info("Filters cleared");
            refetch();
          }}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2.5 rounded-md shadow-sm transition-all duration-200 whitespace-nowrap"
        >
          Clear
        </button> */}
      </div>
    </div>
  );

  return (
    <>
      {isAllowed ? (
        <PageShell
          title={t("user_management")}
          headerActions={headerActions}
          breadcrumbs={!isMobile ? <FTPBreadCrumbs crumbs={crumbs} /> : null}
          contentPadding="px-4"
        >
          <div className="rounded-lg flex flex-col">
            <TeamDetails
              userDetails={userData}
              selectData={getData}
              hydrate={reftechFullProject}
              ActiveList={TeamList}
            />

            <UserDetails />
            {isSuccess ? (
              // <UsersListTable users={data?.data?.users} onRefresh={refetch} />
              <UsersList users={data?.data?.users} onRefresh={refetch} />
            ) : null}
            <AddNewUsers onUserAdded={refetch} />

            <SideOverlay
              onClose={handleAddTeamClose}
              show={addTeam}
              title={t("add_new")}
            >
              <AddTeam
                hydrate={reftechFullProject}
                setTeamData={getTeamData}
                userFilterData={userFilterData}
                userDetails={userData}
                edit={editTeam}
              />
            </SideOverlay>
          </div>
        </PageShell>
      ) : (
        <div className="p-4 text-md font-bold text-gray-500">
          {t("permission")}
        </div>
      )}

      {voterListStore.connect_user_to_voterlist_modal && (
        <CreateModel
          isOpen={voterListStore.connect_user_to_voterlist_modal}
          desc={`Are you sure you want to Attach this voter to UserProfile ?`}
          onCreateClick={UserAttachVoterProfile}
          onCancelClick={() =>
            voterListStore.update_connect_user_to_voterlist_modal(false)
          }
        />
      )}
    </>
  );
});

export default UserManagement;
